// Pure helpers for the C_LEVEL target-setting screen. Extracted from the .vue so
// the grouping/diff/derive logic is unit-documentable (see kpiTargets.spec.js),
// mirroring the doctorMonthOverview.js + .spec.js split.

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

// A blank editable row: no monthly rate, no per-month overrides. All 12 override
// keys are pre-created so Vue 2 tracks them reactively without $set.
function blankRow() {
  const overrides = {};
  MONTHS.forEach((m) => (overrides[m] = null));
  return { monthly: null, overrides };
}

// Build the editable model from a GET /targets `targets` array + the clinic's
// branch ids. Branches with no stored rows get a blank row (empty inputs). A
// stored override's periodKey "YYYY-MM" maps to overrides[<month-number>].
export function groupTargetsToModel(rows, branchIds) {
  const model = { clinic: blankRow(), branches: {} };
  (branchIds || []).forEach((id) => (model.branches[id] = blankRow()));

  (rows || []).forEach((r) => {
    const row = r.level === "clinic" ? model.clinic : model.branches[r.branchId];
    if (!row) return; // branch not in this clinic's list — ignore defensively
    if (r.periodType === "monthly") {
      row.monthly = r.targetValue;
    } else if (r.periodType === "month" && typeof r.periodKey === "string") {
      const m = Number(r.periodKey.slice(5, 7));
      if (m >= 1 && m <= 12) row.overrides[m] = r.targetValue;
    }
  });
  return model;
}

// Σ = monthly×(12 − #overrides) + Σ overrides. Only cells with a positive value
// count as overrides; blank/0 falls back to the monthly rate.
export function deriveYearTotal(row) {
  const monthly = Number(row.monthly) || 0;
  const ov = MONTHS.map((m) => Number(row.overrides[m]) || 0).filter((v) => v > 0);
  return monthly * (12 - ov.length) + ov.reduce((a, b) => a + b, 0);
}

// Normalize a cell to integer baht; blank/negative/NaN → 0 (clear).
function amount(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
}

// Build the PUT `items` payload from changed cells only (model vs. baseline).
// blank/0 → targetValue 0 (clear). `year` is the selected year (number/string).
export function buildSaveItems(model, baseline, year) {
  const items = [];
  const yKey = String(year);

  const pushIfChanged = (cur, base, item) => {
    if (amount(cur) !== amount(base)) items.push({ ...item, targetValue: amount(cur) });
  };

  const scopeItems = (cur, base, level, branchId) => {
    pushIfChanged(cur.monthly, base.monthly, {
      metricKey: "revenue", level, branchId, periodType: "monthly", periodKey: yKey,
    });
    MONTHS.forEach((m) => {
      pushIfChanged(cur.overrides[m], base.overrides[m], {
        metricKey: "revenue", level, branchId, periodType: "month",
        periodKey: `${yKey}-${String(m).padStart(2, "0")}`,
      });
    });
  };

  scopeItems(model.clinic, baseline.clinic, "clinic", null);
  Object.keys(model.branches).forEach((id) => {
    // A branch present only in `model` (not in `baseline`) is treated as
    // all-blank baseline, so its non-blank cells become save items.
    const base = baseline.branches[id] || { monthly: null, overrides: {} };
    scopeItems(model.branches[id], base, "branch", Number(id));
  });
  return items;
}
